/**
 * AnimatedTransition — Smooth step-to-step transitions
 *
 * Wraps step content with slide/fade animations based on navigation direction.
 */

import { useEffect, useState, useRef } from 'preact/hooks'
import type { ComponentChildren } from 'preact'

interface AnimatedTransitionProps {
  children: ComponentChildren
  stepKey: string
  direction: 'forward' | 'backward'
}

export function AnimatedTransition({ children, stepKey, direction }: AnimatedTransitionProps) {
  const [displayedChildren, setDisplayedChildren] = useState<ComponentChildren>(children)
  const [animationClass, setAnimationClass] = useState('')
  const isFirstRender = useRef(true)
  const prevStepKey = useRef(stepKey)

  // Keep children in sync within the same step (no animation)
  useEffect(() => {
    if (prevStepKey.current === stepKey) {
      setDisplayedChildren(children)
    }
  }, [children, stepKey])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      setDisplayedChildren(children)
      setAnimationClass('bw2-step-enter-active')
      prevStepKey.current = stepKey
      return
    }

    if (prevStepKey.current === stepKey) {
      return
    }

    prevStepKey.current = stepKey

    // Exit animation
    setAnimationClass('bw2-step-exit-active')

    const exitTimer = setTimeout(() => {
      setDisplayedChildren(children)
      const enterClass = direction === 'backward'
        ? 'bw2-step-enter--reverse'
        : 'bw2-step-enter'
      setAnimationClass(enterClass)

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const activeClass = direction === 'backward'
            ? 'bw2-step-enter-active--reverse'
            : 'bw2-step-enter-active'
          setAnimationClass(activeClass)
        })
      })
    }, 200)

    return () => clearTimeout(exitTimer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey])

  return (
    <div className={animationClass} style={{ willChange: 'transform, opacity' }}>
      {displayedChildren}
    </div>
  )
}
